/**
 * Test data generator for quick show creation in development/testing
 * Generates realistic but random show data for rapid testing
 */

import type { ShowFormData } from '../components/ShowForm';
import { localDateToDateOnly } from './dateOnly';

/**
 * Sample show titles for variety
 */
const SHOW_TITLES = [
  'The Midnight Revival',
  'Electric Night Festival',
  'Jazz Under Stars',
  'Rock & Roll Extravaganza',
  'Comedy Night Live',
  'The Symphony Sessions',
  'Indie Artist Showcase',
  'Acoustic Evening',
  'Dance Party 2026',
  'Vintage Vinyl Night',
  'The Reunion Tour',
  'Neon Nights Live',
  'Desert Sounds Festival',
  'Urban Beats Collective',
  'The Grand Concert',
];

/**
 * Show types for variety
 */
const SHOW_TYPES = ['concert', 'festival', 'private_event', 'corporate', 'other'];

/**
 * Generate a random show title
 */
const getRandomTitle = (): string => {
  return SHOW_TITLES[Math.floor(Math.random() * SHOW_TITLES.length)];
};

/**
 * Generate a random show type
 */
const getRandomShowType = (): string => {
  return SHOW_TYPES[Math.floor(Math.random() * SHOW_TYPES.length)];
};

/**
 * Generate a random time between 09:00 and 22:00
 */
const getRandomTime = (): string => {
  const hour = Math.floor(Math.random() * 14) + 9; // 9 AM to 11 PM
  const minute = Math.random() > 0.5 ? '00' : '30';
  return `${hour.toString().padStart(2, '0')}:${minute}`;
};

/**
 * Generate a random future date (next 5-14 days)
 */
const getRandomFutureDate = (): string => {
  const today = new Date();
  const daysAhead = Math.floor(Math.random() * 10) + 5; // 5-14 days
  const futureDate = new Date(today);
  futureDate.setDate(futureDate.getDate() + daysAhead);
  return localDateToDateOnly(futureDate);
};

/**
 * Generate a random currency amount
 */
const getRandomAmount = (min: number, max: number): string => {
  const amount = Math.random() * (max - min) + min;
  return amount.toFixed(2);
};

/**
 * Generate a random integer
 */
const getRandomInt = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Generate realistic show data with random values
 * Times are offset with load-in earliest, then soundcheck, doors, show
 */
export const generateRandomShowData = (): Partial<ShowFormData> => {
  const baseTime = getRandomTime();
  const [baseHour] = baseTime.split(':').map(Number);

  // Create time offsets
  const loadInHour = Math.max(baseHour - 4, 0); // 4 hours before
  const soundcheckHour = baseHour - 2; // 2 hours before
  const doorsHour = baseHour - 1; // 1 hour before
  const showHour = baseHour;

  const formatTime = (hour: number, minute = 0): string =>
    `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

  // Capacity determines realistic sales
  const capacity = getRandomInt(200, 1500);
  const advanceSold = getRandomInt(Math.round(capacity * 0.3), Math.round(capacity * 0.8));
  const doorSold = getRandomInt(Math.round(capacity * 0.05), Math.round(capacity * 0.2));
  const comps = getRandomInt(0, Math.round(capacity * 0.05));
  const totalAttendance = Math.min(advanceSold + doorSold + comps, capacity);

  // Revenue based on attendance and ticket price
  const ticketPrice = Math.round(getRandomAmount(15, 65) as any);
  const ticketRevenue = (totalAttendance * ticketPrice).toFixed(2);
  const barRevenue = getRandomAmount(totalAttendance * 8, totalAttendance * 20);
  const otherRevenue = getRandomAmount(0, totalAttendance * 5);

  // Expenses
  const venueCost = getRandomAmount(500, 3000);
  const productionCost = getRandomAmount(1000, 5000);
  const marketingCost = getRandomAmount(200, 1500);
  const otherCost = getRandomAmount(100, 1000);

  return {
    title: getRandomTitle(),
    show_date: getRandomFutureDate(),
    show_time: formatTime(showHour),
    doors_time: formatTime(doorsHour),
    soundcheck_time: formatTime(soundcheckHour),
    load_in_time: formatTime(loadInHour),
    status: 'tentative',
    show_type: getRandomShowType(),
    ticket_price: ticketPrice.toString(),
    ticket_capacity: capacity,
    advance_tickets_sold: advanceSold,
    door_tickets_sold: doorSold,
    comps_given: comps,
    total_attendance: totalAttendance,
    ticket_revenue: ticketRevenue,
    bar_revenue: barRevenue,
    other_revenue: otherRevenue,
    venue_expenses: venueCost,
    production_expenses: productionCost,
    marketing_expenses: marketingCost,
    other_expenses: otherCost,
    show_notes: `Test show generated at ${new Date().toLocaleTimeString()}. Random data for testing purposes.`,
    settlement_notes: '',
    is_settled: false,
  };
};
